import type { ComboboxOption } from '@sparklayer-io/spark-dash-ui/components/ui/combobox/types';
import type { RadioSetOption } from '@sparklayer-io/spark-dash-ui/components/ui/radio-set/types';
import {
  Alert,
  Button,
  Card,
  Combobox,
  Form,
  LearnMoreLink,
  RadioSet,
  Skeleton,
  Text,
} from '@ui/themed';
import { t } from 'i18next';
import { type Dispatch, type SetStateAction, useMemo, useState } from 'react';
import { useHandlers } from '../../../context/page-context';
import type { I18nPrefix } from '../../../i18n/prefix';
import { useCustomerRoles } from '../hooks/use-customer-roles';
import { useSalesAgentsGroups } from '../hooks/use-sales-agent-groups';
import { useUpdateCustomer } from '../hooks/use-update-customer';

function getRoleOption(slug: string, name: string): RadioSetOption {
  const baseKey = 'features:customers.components.customer_role_selection_card' satisfies I18nPrefix;
  let label = name;
  let description = t(`${baseKey}.custom.description`);
  switch (slug) {
    case 'main':
      label = t(`${baseKey}.main.label`);
      description = t(`${baseKey}.main.description`);
      break;
    case 'sales-agent':
      label = t(`${baseKey}.sales_agent.label`);
      description = t(`${baseKey}.sales_agent.description`);
      break;
    case 'sales-admin':
      label = t(`${baseKey}.sales_admin.label`);
      description = t(`${baseKey}.sales_admin.description`);
      break;
  }

  return { value: slug, label, description };
}

function RestrictSalesAgentGroupsAccessAlert({
  selectedGroups,
  isLoading,
  isError,
  setGroups,
  refetch,
  allSalesAgentGroups = [],
}: {
  selectedGroups: string[];
  isLoading: boolean;
  isError: boolean;
  setGroups: Dispatch<SetStateAction<string[]>>;
  refetch: () => void;
  allSalesAgentGroups?: string[];
}) {
  if (isLoading) {
    return <Skeleton />;
  }

  if (isError) {
    return (
      <Alert
        headingLevel="h3"
        variant="error"
        title={t(
          'features:customers.components.customer_role_selection_card.restrict_sales_agent_groups_access_alert.error.title',
        )}
      >
        <div className="flex flex-col">
          <Text>
            {t(
              'features:customers.components.customer_role_selection_card.restrict_sales_agent_groups_access_alert.error.description',
            )}
          </Text>
          <span className="flex justify-end">
            <Button onClick={refetch} icon="refresh" variant="secondary">
              {t('buttons:reload')}
            </Button>
          </span>
        </div>
      </Alert>
    );
  }

  const options = allSalesAgentGroups.map(
    (group): ComboboxOption => ({
      label: group,
      value: group,
    }),
  );

  return (
    <Alert headingLevel="h3">
      <div className="flex flex-col gap-2">
        <span>
          {t(
            'features:customers.components.customer_role_selection_card.restrict_sales_agent_groups_access_alert.description',
          )}{' '}
          <LearnMoreLink link="https://docs.sparklayer.io/sales-agent-ordering" />
        </span>
        <Combobox
          multiple
          options={options ?? []}
          value={selectedGroups}
          onChange={setGroups}
          placeholder={t(
            'features:customers.components.customer_role_selection_card.restrict_sales_agent_groups_access_alert.placeholder',
          )}
        />
      </div>
    </Alert>
  );
}

export function CustomerRoleSelectionCard({
  sparkId,
  initialRole,
  initialSalesAgentGroups,
}: {
  sparkId: string;
  initialRole: string;
  initialSalesAgentGroups: string[];
}) {
  const { notificationHandler } = useHandlers();
  const [salesAgentGroups, setSalesAgentGroups] = useState<string[]>(initialSalesAgentGroups);
  const [role, setRole] = useState<string>(initialRole);
  const { data: roles = [], isLoading: rolesLoading, isError: rolesError } = useCustomerRoles();
  const roleOptions: RadioSetOption<string>[] = useMemo(() => {
    const relevant = roles.filter(
      (r) => (r.type === 'customer' && r.slug === 'main') || r.type === 'sales',
    );
    const priority = (r: (typeof relevant)[number]) => {
      if (r.type === 'customer') return 0;
      if (r.slug === 'sales-agent') return 1;
      if (r.slug === 'sales-admin') return 2;
      return 3;
    };
    return [...relevant]
      .sort((a, b) => priority(a) - priority(b))
      .map((r) => getRoleOption(r.slug, r.name));
  }, [roles]);
  const isSalesRole = useMemo(
    () => roles.find((r) => r.slug === role)?.type === 'sales',
    [roles, role],
  );
  const { mutate: updateCustomer, isPending } = useUpdateCustomer({
    lookup: {
      identifier: sparkId,
      by: 'sparklayer',
    },
  });
  const {
    data: allSalesAgentGroups,
    isLoading,
    isError,
    refetch,
  } = useSalesAgentsGroups(isSalesRole);

  function handleUpdate(roleSlug: string, salesAgentGroups: string[]) {
    const roleName = roleOptions.find((r) => r.value === roleSlug)?.label;
    const payload = isSalesRole
      ? { role: roleSlug, sales_agent_groups: salesAgentGroups }
      : { role: roleSlug };
    updateCustomer(payload, {
      onError: () =>
        notificationHandler.error(
          t(
            'features:customers.components.customer_role_selection_card.notifications.error.role_update_failed',
            { role: roleName },
          ),
        ),
      onSuccess: () => {
        notificationHandler.success(
          t(
            'features:customers.components.customer_role_selection_card.notifications.success.role_update_succeeded',
            { role: roleName },
          ),
        );
      },
    });
  }

  return (
    <Card>
      <Card.Header headingLevel="h2">
        {t('features:customers.components.customer_role_selection_card.header')}
      </Card.Header>
      <Card.Body>
        <Text variant="secondary">
          {t('features:customers.components.customer_role_selection_card.description')}{' '}
          <LearnMoreLink link="https://docs.sparklayer.io/sales-agent-ordering" />
        </Text>
        <Form gap="sm" onSubmit={() => handleUpdate(role, salesAgentGroups)}>
          {rolesLoading ? (
            <Skeleton />
          ) : rolesError ? (
            <Alert
              headingLevel="h3"
              variant="error"
              title={t(
                'features:customers.components.customer_role_selection_card.roles_load_error.title',
              )}
            >
              <Text>
                {t(
                  'features:customers.components.customer_role_selection_card.roles_load_error.description',
                )}
              </Text>
            </Alert>
          ) : (
            <RadioSet value={role} options={roleOptions} onChange={(v) => setRole(v)} />
          )}
          {isSalesRole && (
            <RestrictSalesAgentGroupsAccessAlert
              allSalesAgentGroups={allSalesAgentGroups ?? undefined}
              selectedGroups={salesAgentGroups}
              setGroups={setSalesAgentGroups}
              isLoading={isLoading}
              isError={isError}
              refetch={() => void refetch()}
            />
          )}
          <div className="flex justify-end">
            <Button
              type="submit"
              disabled={rolesError || rolesLoading || isError || isLoading}
              loading={isPending}
            >
              {t('features:customers.components.customer_role_selection_card.save_role')}
            </Button>
          </div>
        </Form>
      </Card.Body>
    </Card>
  );
}
